<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import PublisherCard from './PublisherCard.svelte';
  import VideoCaption from './VideoCaption.svelte';
  import Liked from '$lib/assets/icons/Liked.svelte';
  import LikeOutlined from '$lib/assets/icons/LikeOutlined.svelte';
  import ShareIcon from '$lib/assets/icons/ShareIcon.svelte';
  import { SECONDS_IN_A_DAY } from '$lib/constants';
  import { sendAnalyticsEvent } from '$lib/services/analytics';
  import { EventNames, EventSectionName } from '$lib/types/analytics';
  import { PersistentStore } from '$lib/utils/persistent-store';
  import { onShare, sendContentSharedEvent } from '$lib/utils/share';
  import CloseIcon from '$lib/assets/icons/CloseIcon.svelte';
  import RetryIcon from '$lib/assets/icons/RetryIcon.svelte';
  import { VIDEO_PLAYING_ERROR_MESSAGE } from '$lib/constants/text';
  import VideoProgressBar from './VideoProgressBar.svelte';
  import { videoControl } from '$lib/store';
  import { createNewsDetailsUrl } from '$lib/utils/news';

  export let publisherLogo: string;
  export let publisherName: string;
  export let headline: string;
  export let videoUrl: string | null;
  export let showModal = false;
  export let position: number;
  export let sourceUrl: string;
  export let postId: string;
  export let posterImage: string;
  export let category: string;

  let videoPlayingErrorText = VIDEO_PLAYING_ERROR_MESSAGE;
  let videoElement: HTMLVideoElement | null = null;
  let duration = 0;
  let interval: ReturnType<typeof setInterval>;
  let timeSpentInterval: ReturnType<typeof setInterval>;
  let timeSpendOnModal = 0;
  let videoElementID = 'video-player-element';

  $: currentTime = 0;
  $: isPlaying = true;

  onMount(() => {
    const unsubscribe = videoControl.subscribe((value) => {
      if (value) videoElement?.play();
      else videoElement?.pause();
    });

    return () => unsubscribe();
  });

  function onPlayPause() {
    if ($videoControl && videoElement) {
      if (!videoElement.paused) {
        videoElement.pause();
        updateSeeker();
      } else {
        videoElement.play();
        updateSeeker();
      }
      isPlaying = !isPlaying;
      sendAnalyticsEvent(EventNames.VIDEO_PLAY, {
        section_name: EventSectionName.VIDEO_NEWS,
        card_position: position + 1,
        video_length: duration,
        played_time: timeSpendOnModal,
        cta_text: 'Play'
      });
    }
  }

  function onSeek(time: number) {
    if (videoElement) {
      videoElement.currentTime = time;
    }
  }

  function updateSeeker() {
    if (videoElement) {
      currentTime = videoElement.currentTime;
      duration = videoElement.duration;
    }
  }

  onMount(() => {
    if (PersistentStore.get(postId) === 'LIKED') {
      isLiked = true;
    }
    if (videoElement) {
      interval = setInterval(updateSeeker, 1000);
    }
    timeSpentInterval = setInterval(() => {
      timeSpendOnModal = timeSpendOnModal + 1;
    }, 1000);
  });

  onDestroy(() => {
    if (interval) {
      clearInterval(interval);
    }
  });

  // Reset the video and seeker when it reaches the end
  $: {
    if (videoElement && currentTime === duration) {
      isPlaying = false;
    }
  }

  let isLiked = false;
  $: postId = postId;

  const handleLike = () => {
    if (isLiked) {
      PersistentStore.remove(postId);
      isLiked = false;
      return;
    }
    isLiked = true;
    PersistentStore.set(postId, 'LIKED', { expiryTime: 2 * SECONDS_IN_A_DAY });
    sendAnalyticsEvent(EventNames.LIKE_CLICKED, { url: sourceUrl });
  };

  const handleOnShare = () => {
    onPlayPause();
    sendAnalyticsEvent(EventNames.SHARE_CLICK, { url: sourceUrl });
    // try {
    //   window.MySitesAndroidInterface.onShare(
    //     `${$page.url.origin}createNewsDetailsUrl(category, postId)`
    //   );
    // } catch (error) {
    onShare({ url: createNewsDetailsUrl(category, postId) || '' }, sendContentSharedEvent);
    // }
  };

  const closeModal = () => {
    sendAnalyticsEvent(EventNames.VIDEO_CLOSED, {
      section_name: EventSectionName.VIDEO_NEWS,
      card_position: position + 1,
      video_length: duration,
      played_time: timeSpendOnModal,
      cta_text: 'Close'
    });
    clearInterval(timeSpentInterval);
    showModal = false;
  };

  $: isVideoError = false;

  function handleVideoError(error: Event & { currentTarget: EventTarget & Element }) {
    isVideoError = true;
    sendAnalyticsEvent(EventNames.VIDEO_PLAYING_ERROR, {
      section_name: EventSectionName.VIDEO_NEWS,
      card_position: position + 1,
      video_length: duration,
      played_time: timeSpendOnModal,
      error
    });
  }

  function handleVideoCanPlay() {
    isVideoError = false;
  }

  function resetVideoPlayer() {
    // Pause the video (if it's playing)
    if (videoElement && !videoElement.paused) {
      videoElement.pause();
    }

    // Reset the video's currentTime
    if (videoElement) {
      videoElement.currentTime = 0;
    }

    // Reset the video loading state
    isVideoError = false;
  }
</script>

<div class="video-player">
  <button class="icon-wrapper" on:click={closeModal}>
    <CloseIcon />
  </button>
  {#if !isVideoError}
    <div>
      <button class="wrapper-button-container" on:click={onPlayPause}>
        <video
          id={videoElementID}
          autoplay
          preload="auto"
          poster={posterImage}
          bind:this={videoElement}
          on:error={(error) => handleVideoError(error)}
          on:canplay={handleVideoCanPlay}
        >
          <track kind="captions" />
          <source src={videoUrl} type="video/mp4" />
        </video>
        <!--! removing instream ads -->
        <!-- <div class="instream-ad-container">
          <AdSlot
            adUnitName={VideoInstreamAds.NEWS_VIDEOCONTENT_PREROLL_INSTREAM}
            adType="instream"
            categoryName={AdCategoryNames.NEWS}
            {videoElementID}
          />
        </div> -->
      </button>

      <div class="video-details">
        <div class="publisher-details">
          <PublisherCard {publisherLogo} {publisherName} />
          <VideoCaption {headline} />
        </div>
      </div>
      <div class="interaction-icons">
        <button class="like-icon-container" on:click={handleLike}>
          {#if isLiked}
            <Liked />
          {:else}
            <LikeOutlined />
          {/if}
        </button>
        <button on:click={handleOnShare} class="share-icon-container">
          <ShareIcon />
        </button>
      </div>
      <div class="video-seeker">
        <VideoProgressBar
          {videoElement}
          {currentTime}
          {duration}
          {isPlaying}
          {onSeek}
          {onPlayPause}
        />
      </div>
    </div>
  {:else}
    <div class="error-message-container">
      <h5 class="error-message">{videoPlayingErrorText}</h5>
      <button class="retry-button" on:click={resetVideoPlayer}>
        <RetryIcon />
      </button>
    </div>
  {/if}
</div>

<style lang="scss">
  .video-player {
    margin-bottom: 70px;
  }

  video {
    border-radius: 10px;
    width: 100%;
    height: 250px;
  }

  .video-details {
    position: absolute;
    bottom: 7%;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 0px 10px 10px;
    padding: 10px;
  }

  .video-seeker {
    position: absolute;
    bottom: 3%;
    width: calc(100% - 2.5rem);
    margin-left: 10px;
    padding: 10px;
  }

  .share-icon-container {
    position: absolute;
    top: 81px;
    right: 0px;
  }
  .like-icon-container {
    position: absolute;
    top: 34px;
    right: 0px;
  }

  .share-icon-container,
  .like-icon-container {
    background-color: var(--bg-color);
    width: 35px;
    height: 35px;
    border-radius: 50%;
    border: none;
    background: var(--color-card-background);
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }
  .interaction-icons {
    position: relative;
    margin: 0px 10px;
  }
  .icon-wrapper {
    position: absolute;
    top: 15px;
    right: 15px;
    border-radius: 50%;
    height: 30px;
    width: 30px;
    padding: 8px;
    display: flex;
    border: none;
    justify-content: center;
    align-items: center;

    background: var(--color-white);
    opacity: 0.5;
    cursor: pointer;
    z-index: 1;
  }
  .error-message {
    color: rgb(255, 0, 0);
    font-size: 16px;
    text-align: center;
    margin-top: 20px;
  }

  .error-message-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .retry-button {
    background-color: transparent;
    border: none;
    cursor: pointer;
  }
  .wrapper-button-container {
    background: none;
    color: inherit;
    border: none;
    padding: 0;
    cursor: pointer;
    outline: inherit;
  }

  // .instream-ad-container {
  //   display: flex;
  //   align-items: center;
  //   position: absolute;
  //   top: 0;
  //   height: 100%;
  // }
</style>
